import { useState, useRef, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { apiGetAppealStatus } from 'api';

type SubmissionStatus = 'idle' | 'submitting' | 'polling' | 'success' | 'error';

export const useAppealPolling = (uuid: string) => {
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useToast();

  const startPolling = useCallback(
    (logId: number) => {
      setSubmissionStatus('polling');

      const pollStatus = async () => {
        try {
          const response = await apiGetAppealStatus(uuid, logId);

          if (
            response.jira_ticket_key &&
            response.jira_ticket_status?.toLowerCase() === 'open'
          ) {
            clearInterval(pollingIntervalRef.current!);
            setSubmissionStatus('success');
            toast({
              title: 'Appeal submitted successfully',
              description: `Jira ticket has been created`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          if (response.jira_ticket_status?.toLowerCase() === 'error') {
            clearInterval(pollingIntervalRef.current!);
            setSubmissionStatus('error');
            setErrorMessage(
              'Failed to create Jira ticket. Please contact support.',
            );
            toast({
              title: 'Jira ticket creation failed',
              description:
                'Failed to create Jira ticket. Please contact support.',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
            return;
          }
        } catch (error: any) {
          clearInterval(pollingIntervalRef.current!);
          setSubmissionStatus('error');
          const errorMsg = error?.message || 'Failed to check appeal status';
          setErrorMessage(errorMsg);
          toast({
            title: 'Error checking appeal status',
            description: errorMsg,
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
        }
      };

      // Poll every 2 seconds
      pollingIntervalRef.current = setInterval(pollStatus, 2000);
      // Start immediately
      pollStatus();
    },
    [uuid, toast],
  );

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
  }, []);

  const resetPollingState = useCallback(() => {
    stopPolling();
    setSubmissionStatus('idle');
    setErrorMessage(null);
  }, [stopPolling]);

  return {
    submissionStatus,
    setSubmissionStatus,
    errorMessage,
    startPolling,
    stopPolling,
    resetPollingState,
  };
};
